import axios, { AxiosResponse } from 'axios'
import { Component } from 'preact'
import { checkAttachments } from '@insided/shared/uikit/Editor/Attachments/utils'

import Editor from './index.js'
import redirect from '../../utils/redirect'
import { publish } from '../../utils/globals'
import { PageUnloadContext } from '@insided/shared/services/PageUnloadContext'

type PostRequestResponse = AxiosResponse<{
  errors: string[]
  success: string | undefined
}>
type PostRequest = (content: string, attachments: Array<{}>, hasFileInProgress: boolean) => Promise<PostRequestResponse>

function newPost (): PostRequest {
  return async (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return await axios.post(window.location.pathname.split('?')[0], {
      pagetext: content,
      attachments
    })
  }
}

function editPost (topicId: number, postId: number): PostRequest {
  return async (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return await axios.post('/post/edit', {
      NewPostForm: {
        pagetext: content
      },
      attachments: attachments
    }, {
      params: {
        postid: postId,
        tid: topicId
      }
    })
  }
}

function newPostDestination (topicId: number): PostRequest {
  return async (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return await axios.post('/post/save', {
      post: { content },
      attachments
    }, {
      params: {
        topic_id: topicId
      }
    })
  }
}

function editPostDestination (topicId: number, postId: number): PostRequest {
  return async (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return await axios.post('/post/update', {
      post: { content },
      attachments
    }, {
      params: {
        post_id: postId,
        tid: topicId
      }
    })
  }
}

function determineRequest ({ postId, isControl, topicId }: {postId: number, isControl: boolean, topicId: number}) {
  if (!isControl) {
    if (postId) return editPostDestination(topicId, postId)
    return newPostDestination(topicId)
  }
  if (postId) return editPost(topicId, postId)
  return newPost()
}

export interface PostEditorProps {
  postId: number
  topicId: number
  isControl: boolean
  redirect: boolean
  autoFocus?: boolean
  onSuccess: (content: string, response: PostRequestResponse) => void
  onCancel?: () => void
  disableFileAttachments: boolean
  data?: string
}

export interface PostEditorState {
  attachments: Array<{}>
  errors: Array<{errorMessage: string}>
  isLoading: boolean
  content: string
}

export default class PostEditor extends Component<PostEditorProps, PostEditorState> {
  constructor (props: PostEditorProps) {
    super(props)
    this.state = {
      attachments: [],
      errors: [],
      isLoading: false,
      content: props.data ?? ''
    }
    this.onChange = this.onChange.bind(this)
    this.onCancel = this.onCancel.bind(this)
  }

  async componentDidMount () {
    const postId = this.props.postId
    if (!postId) return

    const { data: attachments } = await axios.get(`/attachment/getForPost?postIds=${postId}`)
    if (!attachments || !attachments[postId]) return

    this.setState({ attachments: attachments[postId] })
  }

  onChange (content: string, attachments: Array<{}>) {
    this.setState({ content, attachments })
  }

  onCancel () {
    const { onCancel, data } = this.props
    this.setState({ content: data ?? '' })
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  render () {
    const { disableFileAttachments, isControl, autoFocus, onSuccess, onCancel, data } = this.props
    const { content } = this.state
    const submitRequest = determineRequest(this.props)

    return (
      <PageUnloadContext.Consumer>
        {({ lock, unlock }) => {
          return (
            <Editor
              {...this.props}
              disableFileAttachments={disableFileAttachments}
              isControl={isControl}
              autoFocus={!!autoFocus}
              errors={this.state.errors}
              isLoading={this.state.isLoading}
              attachments={this.state.attachments}
              onChange={(content: string, attachments: Array<{}>) => {
                this.onChange(content, attachments)
                lock(content, data)
              }}
              onCancel={onCancel ? () => {
                unlock()
                this.onCancel()
              } : null}
              data={content ?? ''}
              onSubmit={async (content: string, attachments: Array<{}>, hasFileInProgress: boolean) => {
                this.setState({
                  isLoading: true
                })
                try {
                  const response = await submitRequest(content, attachments.map(item => JSON.stringify(item)), hasFileInProgress)
                  const errors = response.data.errors
                  if (errors?.length) {
                    const newErrors = errors.map(error => {
                      return { errorMessage: error }
                    })
                    this.setState({
                      errors: this.state.errors.concat(newErrors),
                      isLoading: false
                    })
                    return
                  }
                  unlock()
                  publish('post_editor_submit', { content })

                  if (this.props.redirect) {
                    return redirect(response.data.success ?? response.headers.location)
                  }
                  onSuccess(content, response)
                  this.setState({ errors: [] })
                  window.CKEDITOR.instances.editor1.setData('')
                } catch (error) {
                  const errorMessage = error.response.data?.errors?.pagetext || error.response.data?.errors?.content || error.response.data?.errors || 'Something\'s gone wrong'
                  this.setState({
                    errors: this.state.errors.concat({
                      errorMessage
                    })
                  })
                }
                this.setState({
                  isLoading: false
                })
              }}
            />
          )
        }}
      </PageUnloadContext.Consumer>
    )
  }
}
